import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, inject, Modal, ModalHandle, TasksStore } from 'Mri.Web.Common';
import * as React from 'react';
import { LinkContainer } from 'react-router-bootstrap';
import { AccountsStore } from '../Accounts/AccountsStore';
import { AppStore } from '../Core/AppStore';
import { UsersStore } from './UsersStore';
import { BsCheck } from '@react-icons/all-files/bs/BsCheck';
import { BsTrash } from '@react-icons/all-files/bs/BsTrash';
import { BsUnlock } from '@react-icons/all-files/bs/BsUnlock';
import { BsLock } from '@react-icons/all-files/bs/BsLock';

export interface IUserCardProps {
    readonly user: Mri.Web.Portal.Controllers.Account.UserModel;
    readonly className?: string;
}

@autoinject
@observer
export class UserCard extends React.Component<IUserCardProps> {
    public render() {
        return (
            <div className="card">
                <div className="card-body p-2">
                    <div>
                        <b>UserName: </b>
                        {this.props.user.userName}
                    </div>
                    <div>
                        <b>Email: </b>
                        {this.props.user.email}
                        {this.props.user.emailConfirmed && <BsCheck />}
                    </div>
                    <div>
                        <b>Roles: </b>
                        {this.props.user.roles.join(', ')}
                    </div>
                    <div>
                        <b>Accounts: </b>
                        {this.accountNames.join(', ')}
                    </div>
                </div>

                <div className="card-footer d-flex justify-content-around p-1">
                    <button className="btn btn-link" onClick={this.delete}>
                        <BsTrash className="text-danger f-20" />
                    </button>

                    <LinkContainer to={`/users/${this.props.user.id}/edit`}>
                        <a className="btn btn-link">Edit</a>
                    </LinkContainer>

                    {this.props.user.lockedOut && (
                        <button className="btn btn-link" onClick={this.unlock}>
                            <BsUnlock className="f-20" />
                        </button>
                    )}
                    {!this.props.user.lockedOut && (
                        <button className="btn btn-link" onClick={this.lock}>
                            <BsLock className=" f-20" />
                        </button>
                    )}
                </div>

                <Modal
                    className="modal-sm"
                    title="Confirm Deletion"
                    modalHandle={this.modalHandle}
                    btnClass="w-50"
                    footerClass="d-flex justify-content-center"
                >
                    <div className="text-center">{'Delete ' + this.props.user.userName + '?'}</div>
                </Modal>
            </div>
        );
    }

    @bind
    private async delete(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        if (await this.modalHandle.confirm()) {
            const task = this.tasksStore.startSessionTask(`Delete User ${this.props.user.userName}`);

            try {
                this.usersStore.deleteAsync(this.props.user);
                task.complete();
            } catch (e: any) {
                task.error(e);
            }
        }
    }

    @bind
    private async unlock(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        const task = this.tasksStore.startSessionTask(`Unlock User ${this.props.user.userName}`);

        try {
            this.usersStore.lockAsync(this.props.user, false);
            task.complete();
        } catch (e: any) {
            task.error(e);
        }
    }

    @bind
    private async lock(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        const task = this.tasksStore.startSessionTask(`Lock User ${this.props.user.userName}`);

        try {
            this.usersStore.lockAsync(this.props.user, true);
            task.complete();
        } catch (e: any) {
            task.error(e);
        }
    }

    @computed
    private get accountNames() {
        return this.props.user.accounts
            .map(a => this.accountsStore.accounts.get(a))
            .map(a => a?.accountName ?? 'Undefined Account');
    }

    @observable
    private modalHandle = new ModalHandle();
    @inject
    private accountsStore!: AccountsStore;
    @inject
    private usersStore!: UsersStore;
    @inject
    private appStore!: AppStore;
    @inject
    private tasksStore!: TasksStore;
}
